<template>
  <v-container class="pa-0" fluid>
    <v-progress-linear
      v-show="isLoading || isGeneratingPDF"
      indeterminate
      color="blue"
    ></v-progress-linear>
    <div class="d-flex justify-between align-center pt-6 px-8">
      <Button
        data-testid="routinePrint-button-back"
        color="primary"
        icon="mdi-chevron-left"
        plain
        :text="$t('common.back_to_routine')"
        @click="handleBack(routineExecution.id)"
      />
      <Button
        data-testid="routinePrint-button-download"
        color="primary"
        icon="mdi-download"
        :loading="isLoading || isGeneratingPDF"
        :text="$t('common.download_as_pdf')"
        @click="downloadPDF"
      />
    </div>
    <div v-if="!isLoading" class="d-flex align-center flex-column mt-8">
      <div v-if="hasFindingsList">
        <VirtualPage
          :data-testid="`routinePrint-virtualPage-main-${index}`"
          ref="mainPage"
          v-for="(findingsChunk, index) in formattedFindings"
          :key="`chunk-${index}`"
        >
          <template slot="content">
            <MainPageRoutinePrint
              :data-testid="`routinePrint-mainPageRoutinePrint-${index}`"
              showEmptyState
              :findingsChunk="findingsChunk"
              :routineExecution="routineExecution"
              :checklistItems="visibleChecklistItems"
              :findingNumber="index + 1"
              :totalPages="totalPagesNumber"
            />
          </template>
        </VirtualPage>
      </div>

      <div v-if="hasCompletedFindings">
        <VirtualPage
          :data-testid="`routinePrint-virtualPage-completed-${index}`"
          v-for="(findingsChunk, index) in completedFindings"
          ref="completedFindingsPage"
          :key="index"
        >
          <template slot="content">
            <CompletedFindingsPage
              :data-testid="`routinePrint-completedFindingsPage-${index}`"
              :findings="findingsChunk"
              :routineExecution="routineExecution"
              :totalCompletedFindings="totalCompletedFindings"
              :checklistItems="visibleChecklistItems"
              :numberOfFindingsChunk="formattedFindings.length + index + 1"
              :totalPages="totalPagesNumber"
            />
          </template>
        </VirtualPage>
      </div>

      <ChecklistVirtualPage
        v-if="hasChecklistItems"
        ref="checklistPage"
        data-testid="routinePrint-checklistVirtualPage"
      >
        <template slot="content">
          <ChecklistPage
            data-testid="routinePrint-checklistPage"
            :checklistItems="visibleChecklistItems"
            :routineExecution="routineExecution"
            :totalPages="totalPagesNumber"
          />
        </template>
      </ChecklistVirtualPage>

      <VirtualPage v-if="hasApprovals" ref="approvalsPage">
        <template slot="content">
          <ApprovalsPage
            data-testid="routinePrint-approvalsPage"
            :approvals="approvedApprovals"
            :checklistItems="visibleChecklistItems"
            :routineExecution="routineExecution"
            :totalPages="totalPagesNumber"
          />
        </template>
      </VirtualPage>
    </div>

    <div v-if="!isLoading">
      <div v-if="!isGeneratingPDF" ref="mainPages" class="v-application d-none">
        <div>
          <div v-if="hasFindingsList">
            <div
              class="px-8 py-5"
              v-for="(findingsChunk, index) in formattedFindings"
              :key="`chunk-${index}`"
            >
              <MainPageRoutinePrint
                :data-testid="`routinePrint-mainPageRoutinePrint-print-${index}`"
                showEmptyState
                :findingsChunk="findingsChunk"
                :routineExecution="routineExecution"
                :checklistItems="visibleChecklistItems"
                :findingNumber="index + 1"
                :totalPages="totalPagesNumber"
              />
            </div>
          </div>

          <div
            v-if="hasCompletedFindings"
            class="pageBreakBefore"
            data-testid="routinePrint-container-completedFindingsPageBreak"
          >
            <div
              class="px-8 py-5"
              v-for="(findingsChunk, index) in completedFindings"
              :key="index"
            >
              <CompletedFindingsPage
                :data-testid="`routinePrint-completedFindingsPage-print-${index}`"
                :findings="findingsChunk"
                :routineExecution="routineExecution"
                :totalCompletedFindings="totalCompletedFindings"
                :checklistItems="visibleChecklistItems"
                :numberOfFindingsChunk="formattedFindings.length + index + 1"
                :totalPages="totalPagesNumber"
              />
            </div>
          </div>

          <div
            v-if="hasChecklistItems"
            class="pageBreakBefore px-5 py-5"
            data-testid="routinePrint-container-checklistPageBreak"
          >
            <ChecklistPage
              data-testid="routinePrint-checklistPage-print"
              :checklistItems="visibleChecklistItems"
              :routineExecution="routineExecution"
              :totalPages="totalPagesNumber"
            />
          </div>

          <div
            v-if="hasApprovals"
            class="pageBreakBefore px-5 py-5"
            data-testid="routinePrint-container-approvalsPage"
          >
            <ApprovalsPage
              data-testid="routinePrint-approvalsPageHidden"
              :approvals="approvedApprovals"
              :checklistItems="visibleChecklistItems"
              :routineExecution="routineExecution"
              :totalPages="totalPagesNumber"
            />
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import _ from 'lodash'
import html2pdf from 'html2pdf.js'
import { mapGetters } from 'vuex'

import api from '@/api'
import { APPROVAL_STATUS } from '@/containers/RoutineExecutionView/Approvals/constants'
import { hydrateChecklistItems } from '@/components/Checklists/ChecklistReply/checklistHydrationUtils'
  import Button from '@/components/UI/Button'
import VirtualPage from '@/components/VirtualLetterPortraitPage.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ApprovalsPage from './PrintPages/ApprovalsPage.vue'
import ChecklistPage from './PrintPages/ChecklistPage.vue'
import ChecklistVirtualPage from './PrintPages/ChecklistVirtualPage.vue'
import CompletedFindingsPage from './PrintPages/CompletedFindingsPage.vue'
import MainPageRoutinePrint from './PrintPages/MainPageRoutinePrint.vue'

const MAX_FINDINGS_TO_SHOW = {
  COMPLETED_FINDINGS: 6,
}

export default {
  name: 'routinePrintView',

  mixins: [mixin, trackerMixin],

  components: {
    ApprovalsPage,
    Button,
    ChecklistPage,
    ChecklistVirtualPage,
    CompletedFindingsPage,
    MainPageRoutinePrint,
    VirtualPage,
  },

  data() {
    return {
      isLoading: false,
      isGeneratingPDF: false,
      isPristinePDF: true,
      routineExecution: {},
      checklistItems: [],
      approvals: [],
      MAX_FINDINGS_TO_SHOW,
    }
  },

  async mounted() {
    await this.fetchRoutine()
  },

  computed: {
    ...mapGetters(['organizationId']),

    findingsList() {
      return this.routineExecution?.findings ?? []
    },

    formattedFindings() {
      return _.chunk(
        this.findingsList?.map(finding => {
          return {
            id: finding?.id ?? finding?._id ?? null,
            media: finding?.media ?? [],
            situation: finding?.situation ?? null,
            status: finding?.status ?? null,
            assignees: finding?.assignees ?? [],
            dueDate: finding.dueDate,
            completedAt: finding?.completedAt,
            entityType: this.$constants.exploreEntityTypes.finding,
          }
        }) ?? [],
        2
      )
    },

    completedFindings() {
      const completeFindings =
        this.routineExecution?.findings?.filter(
          finding => finding.status === 'completed'
        ) ?? []

      if (_.isEmpty(completeFindings)) return []

      return _.chunk(completeFindings, MAX_FINDINGS_TO_SHOW.COMPLETED_FINDINGS)
    },

    totalCompletedFindings() {
      const completeFindings =
        this.routineExecution?.findings?.filter(
          finding => finding.status === 'completed'
        ) ?? []

      return completeFindings.length
    },

    hasCompletedFindings() {
      return !_.isEmpty(this.completedFindings)
    },

    hasFindingsList() {
      return Boolean(this.formattedFindings?.length > 0)
    },

    hasChecklistItems() {
      return !_.isEmpty(this.visibleChecklistItems)
    },

    visibleChecklistItems() {
      return (this.checklistItems ?? []).filter(
        item => item?.isVisible !== false
      )
    },

    hasApprovals() {
      return !_.isEmpty(this.approvedApprovals)
    },

    approvedApprovals() {
      return (this.approvals ?? []).filter(
        approval => approval?.status === APPROVAL_STATUS.APPROVED
      )
    },

    totalPagesNumber() {
      return (
        this.formattedFindings.length +
        this.completedFindings.length +
        (this.hasChecklistItems ? 1 : 0) +
        (this.hasApprovals ? 1 : 0)
      )
    },
  },

  methods: {
    getChecklistResponseRelations() {
      const relations = ['items.responseImages']

      relations.push('items.signer', 'items.signer.photo')

      return relations
    },

    getChecklistItemsWithSignatureFlagApplied(items = []) {
      return items
    },

    getChecklistItemsForHydration(items = []) {
      return this.getChecklistItemsWithSignatureFlagApplied(items)
    },

    async hydrateAccountChecklistItems(items = []) {
      const { items: hydratedItems, status } = await hydrateChecklistItems(
        this.getChecklistItemsForHydration(items),
        async idsToResolve => {
          const response = await api.account.getAccountsByIds(
            this.organizationId,
            {
              filters: { id: idsToResolve },
              limit: idsToResolve.length,
            }
          )

          return response?.data?.results || []
        }
      )

      if (status === 'fallback') {
        this.showError(this.$t('checklist.print_account_hydration_failed'))
      }

      return hydratedItems
    },

    async fetchChecklistResponse(checklistId, checklistResponseId) {
      const response = await api.routine.getChecklistResponseById(
        this.organizationId,
        checklistId,
        checklistResponseId,
        {
          relations: this.getChecklistResponseRelations(),
        }
      )

      return response?.data
    },

    async fetchFindings(page = 1, findings = []) {
      try {
        const response = await api.findings.getAll({
          organizationId: this.organizationId,
          limit: 20,
          page,
          filters: {
            routine: this.$route.params.routineExecutionId,
          },
          sort: {
            by: 'createdAt',
            order: 'asc',
          },
        })

        const totalRecordsInData = response?.data?.totalRecordsInData ?? 0
        const newFindings = response?.data?.results ?? []
        findings = [...findings, ...newFindings]

        if (findings.length < totalRecordsInData) {
          return this.fetchFindings(page + 1, findings)
        }

        return findings
      } catch (err) {
        this.handleRequestError(err)
        return []
      }
    },

    async fetchApprovals() {
      try {
        const response = await api.routine.getRoutineApprovals(
          this.organizationId,
          this.$route.params.routineExecutionId,
          {
            sort: {
              by: 'createdAt',
              order: 'desc',
            },
            relations: ['createdBy', 'approver'],
            limit: 100,
          }
        )

        this.approvals = response?.data?.results ?? []
      } catch (error) {
        this.approvals = []
        this.handleRequestError(error)
      }
    },

    async fetchRoutine() {
      try {
        this.isLoading = true
        const response = await api.routine.getExecutionById(
          this.organizationId,
          this.$route.params.routineExecutionId,
          {
            relations: ['routineDefinition', 'createdBy', 'site', 'checklist'],
          }
        )

        this.routineExecution = response?.data ?? {}
        this.routineExecution.findings = await this.fetchFindings()
        await this.fetchApprovals()

        const checklist = response?.data?.checklist || []
        if (_.isEmpty(checklist)) return

        const checklistResponseId = response?.data?.checklistResponse ?? null

        if (checklistResponseId) {
          const checklistResponse = await this.fetchChecklistResponse(
            checklist.id,
            checklistResponseId
          )
          this.checklistItems = await this.hydrateAccountChecklistItems(
            checklistResponse?.items || []
          )
          return
        }

        this.checklistItems = await this.hydrateAccountChecklistItems(
          checklist?.items || []
        )
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isLoading = false
        this.trackPrintRoutine({ type: 'Print view' })
      }
    },

    handleBack(id) {
      this.navigateTo(`/routine-executions/${id}`)
    },

    async downloadPDF() {
      try {
        const htmlRef = this.$refs?.mainPages
        this.isGeneratingPDF = true
        await this.$nextTick()

        const opt = {
          x: 0,
          y: 0,
          margin: [5, 0, 5, 0],
          filename: `${this.routineExecution.name}-print.pdf`,
          image: { type: 'png', quality: 0.98 },
          html2canvas: {
            letterRendering: true,
            useCORS: true,
            scale: 1.5,
          },
          jsPDF: {
            unit: 'mm',
            format: 'letter',
            orientation: 'portrait',
            compress: true,
          },
          pagebreak: { mode: ['avoid-all'], before: '.pageBreakBefore' },
        }

        await html2pdf().set(opt).from(htmlRef).save()

        this.trackPrintRoutine({ type: 'Print download' })
      } catch (err) {
        this.showError(err.message)
      } finally {
        this.isGeneratingPDF = false
      }
    },
  },
}
</script>
